"use client";

import { memo, useMemo } from "react";
import * as Popover from "@radix-ui/react-popover";
import type { StudioMemberRole } from "@prisma/client";
import { getInitials } from "@/lib/utils";
import { useOnlinePresence } from "@/lib/realtime/realtime-unread-provider";
import { PresenceDot } from "@/components/presence-dot";
import { ROLE_BADGE_STYLES, ROLE_LABELS } from "@/lib/constants/roles";

type MemberProfilePopoverProps = {
  member: {
    id: string;
    role: StudioMemberRole;
    profile: {
      displayName: string | null;
      image: string | null;
    } | null;
  };
  children: React.ReactNode;
};

function getAvatarSrc(image: string): string {
  if (image.startsWith("http")) {
    return image;
  }
  return `${process.env.NEXT_PUBLIC_CLOUDFRONT_URL}/${image}`;
}

function MemberProfilePopover({ member, children }: MemberProfilePopoverProps) {
  const { onlineMemberIds } = useOnlinePresence();
  const displayName = member.profile?.displayName ?? "Unknown Member";
  const initials = useMemo(() => getInitials(displayName), [displayName]);
  const badgeStyle = ROLE_BADGE_STYLES[member.role];
  const roleLabel = ROLE_LABELS[member.role];
  const isOnline = onlineMemberIds.has(member.id);

  return (
    <Popover.Root>
      <Popover.Trigger asChild>{children}</Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          sideOffset={8}
          collisionPadding={16}
          className="z-50 w-64 rounded-lg border border-border bg-popover p-4 shadow-lg"
        >
          <div className="flex items-center gap-3">
            <div className="relative h-12 w-12 flex-shrink-0 overflow-hidden rounded-md bg-muted">
              {member.profile?.image ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={getAvatarSrc(member.profile.image)}
                  alt={displayName}
                  className="h-full w-full object-cover"
                />
              ) : (
                <span className="flex h-full w-full items-center justify-center text-sm font-semibold text-muted-foreground">
                  {initials}
                </span>
              )}
              <PresenceDot isOnline={isOnline} size="md" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-bold text-foreground">
                {displayName}
              </p>
              <span
                className={`mt-1 inline-block rounded-full px-2 py-0.5 text-xs font-medium ${badgeStyle}`}
              >
                {roleLabel}
              </span>
            </div>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

export default memo(MemberProfilePopover);
